import { useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LocalPoliceOutlinedIcon from "@mui/icons-material/LocalPoliceOutlined";
import { ReportCard } from "../Home/components/ReportCard";
import { ReportCardSkeleton } from "../Home/components/ReportCardSkeleton";
import { CoReviewQueueEmptyState } from "./components/CoReviewQueueEmptyState";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { routeForReport } from "../../shared/routing/routeForReport";
import { useHitlEnabled } from "@/shared/hooks/useHitlEnabled";
import { usePageHeader } from "../../context/LayoutContext";
import {
  inferStationHasRms,
  useCoReviewReports,
} from "./hooks/useCoReviewReports";

type Tab = "pending" | "approved" | "returned" | "committed";

const TAB_STATUSES: Record<Tab, ReportStatus[]> = {
  pending: [ReportStatus.IN_CO_REVIEW],
  approved: [ReportStatus.CO_APPROVED],
  returned: [ReportStatus.CHANGES_REQUESTED],
  committed: [ReportStatus.RMS_COMMITTED],
};

const TAB_LABELS: Record<Tab, string> = {
  pending: "Needs review",
  approved: "Approved",
  returned: "Rejected",
  committed: "RMS Committed",
};

const CoReviewQueuePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const hitlEnabled = useHitlEnabled();
  const { reports, committedTotal, truncated, isLoading, error } =
    useCoReviewReports();
  const [tab, setTab] = useState<Tab>("pending");

  // Working-backlog tabs are counted from the fully-paginated `reports` set, so
  // they are exact. The committed badge uses the authoritative server total
  // (the committed list itself is intentionally bounded to a recent page).
  const counts = useMemo(() => {
    const r = {
      pending: 0,
      approved: 0,
      returned: 0,
      committed: committedTotal,
    };
    for (const rep of reports) {
      if (rep.status === ReportStatus.IN_CO_REVIEW) r.pending++;
      else if (rep.status === ReportStatus.CO_APPROVED) r.approved++;
      else if (rep.status === ReportStatus.CHANGES_REQUESTED) r.returned++;
    }
    return r;
  }, [reports, committedTotal]);

  const totalReports =
    counts.pending + counts.approved + counts.returned + counts.committed;

  usePageHeader({
    title: "CO Review Queue",
    subtitle: "Reports awaiting your review, approval, or feedback",
    icon: <LocalPoliceOutlinedIcon />,
    chip: { label: `${totalReports} report${totalReports === 1 ? "" : "s"}` },
  });

  const stationHasRms = inferStationHasRms(counts.committed);
  const visibleTabs: Tab[] = stationHasRms
    ? ["pending", "approved", "returned", "committed"]
    : ["pending", "approved", "returned"];

  // Guard against a selected tab that is no longer visible (e.g. "committed"
  // disappears after a refresh) so MUI Tabs never gets an out-of-range value.
  const activeTab = visibleTabs.includes(tab) ? tab : "pending";

  const filtered = useMemo(
    () =>
      reports.filter((r) =>
        TAB_STATUSES[activeTab].includes(r.status as ReportStatus),
      ),
    [reports, activeTab],
  );

  // The committed list is intentionally bounded to a recent page; surface that
  // (and the defensive working-backlog cap) rather than silently truncating.
  const committedShown = useMemo(
    () => reports.filter((r) => r.status === ReportStatus.RMS_COMMITTED).length,
    [reports],
  );

  let listNote: string | null = null;
  if (activeTab === "committed" && counts.committed > committedShown) {
    listNote = `Showing the ${committedShown} most recent of ${counts.committed} committed reports.`;
  } else if (truncated && activeTab !== "committed") {
    listNote =
      "Some reports are not shown because an unusually large number are awaiting review.";
  }

  const navigateToReport = (id: string, status: ReportStatus) => {
    // This page only renders behind RequireCoApproval + the manage-station
    // permission, so the CO flags are statically true here.
    navigate(
      routeForReport(
        { id, status },
        { coApprovalEnabled: true, hitlEnabled, isCoView: true },
      ),
    );
  };

  return (
    <Container sx={{ overflow: "auto" }}>
      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          As a Commanding Officer, you can review reports that officers have signed,
          leave inline comments on any section, and either approve them for RMS commit
          or send them back for changes.
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 2,
          mt: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setTab(v as Tab)}
          textColor="primary"
          indicatorColor="primary"
        >
          {visibleTabs.map((key) => (
            <Tab
              key={key}
              value={key}
              label={`${TAB_LABELS[key]} (${counts[key]})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Box>

      {listNote && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {listNote}
        </Typography>
      )}

      {error && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}
        >
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {isLoading ? (
          Array.from(new Array(3)).map((_, i) => (
            <Grid size={{ xs: 12 }} key={i}>
              <ReportCardSkeleton />
            </Grid>
          ))
        ) : filtered.length > 0 ? (
          filtered.map((report) => (
            <Grid size={{ xs: 12 }} key={report.id}>
              <ReportCard
                report={report}
                onOpen={() =>
                  navigateToReport(report.id, report.status as ReportStatus)
                }
              />
            </Grid>
          ))
        ) : (
          <Grid size={{ xs: 12 }}>
            <CoReviewQueueEmptyState tab={activeTab} />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default CoReviewQueuePage;
